import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { XMarkIcon, CubeIcon } from "@heroicons/react/24/outline";
import { Button } from "../../ui";
import VaptStatusBadge from "./VaptStatusBadge";
import { DataTable } from "../../common/DataTable";
import {
  getVaptAssetManagerAssetsApi,
  syncVaptProjectFromAssetManagerApi,
  autoScanVaptProjectApi,
  listVaptProjectsApi,
} from "../../../services/axios/AuthApis/VAPTApis";

// Read-only live Asset Manager inventory viewer for a single customer's
// Asset VAPT engagement. Backed entirely by getVaptAssetManagerAssetsApi
// (live, non-persisting classification of ai_cspm.CloudAsset records) —
// no assets can be added/edited here. "Sync Assets" calls the PERSISTING
// sync (syncVaptProjectFromAssetManagerApi) so a real VAPTProject+targets
// exist to attach a scan to, then refreshes this live view; it is
// explicitly labeled so it reads as a reconciliation action, not a
// destructive/write-heavy one.
const VaptAssetInventoryModal = ({ customerId, customerName, onClose, onNavigateToScan }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState("");
  const [runningScan, setRunningScan] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    getVaptAssetManagerAssetsApi(customerId)
      .then((res) => setData(res))
      .catch((err) =>
        setError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Failed to load asset inventory.")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [customerId]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncNotice("");
    setError(null);
    try {
      const res = await syncVaptProjectFromAssetManagerApi(customerId);
      setSyncNotice(res?.created ? "Asset VAPT project synced from Asset Manager." : (res?.message || "No new assets to sync."));
      load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Failed to sync assets.");
    } finally {
      setSyncing(false);
    }
  };

  // Automatic "Run Scan Now": ensures the engagement/targets exist, then
  // calls the auto-scan endpoint (idempotent — reuses any scan already in
  // flight for this project rather than creating a duplicate) and
  // navigates straight to the resulting scan's detail page. No manual
  // "Sync Assets" click required first.
  const handleRunScanNow = async () => {
    setRunningScan(true);
    setError(null);
    setSyncNotice("");
    try {
      await syncVaptProjectFromAssetManagerApi(customerId);
      const projects = await listVaptProjectsApi({ customer_id: customerId, engagement_type: "asset" });
      const project = (Array.isArray(projects) ? projects : projects?.results || [])[0];
      if (!project?.project_id) {
        setError("No Asset VAPT engagement could be found or created for this customer — no eligible assets are available in the asset inventory.");
        return;
      }
      const scanRes = await autoScanVaptProjectApi(project.project_id);
      onClose && onClose();
      navigate(`/ai-vapt/scans/${scanRes.scan_id}`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Failed to run scan.");
    } finally {
      setRunningScan(false);
    }
  };

  const summary = data?.summary || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <CubeIcon className="w-5 h-5 text-gray-400" />
            <div>
              <h3 className="text-sm font-bold text-gray-900">Live Asset Manager Inventory</h3>
              <p className="text-xs text-gray-500">{customerName || "—"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mb-4">
          Real-time, read-only snapshot of this customer's Asset Manager assets and their VAPT eligibility — nothing
          is persisted by viewing this list.
        </p>

        {loading && <p className="text-sm text-gray-400">Loading asset inventory…</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5 mb-4">{error}</div>
        )}
        {syncNotice && !error && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-lg px-4 py-2.5 mb-4">{syncNotice}</div>
        )}

        {!loading && data && (
          <>
            <div className="flex flex-wrap gap-4 text-sm mb-4">
              <span className="text-gray-600">
                Total: <span className="font-semibold text-gray-900">{summary.total ?? 0}</span>
              </span>
              <span className="text-green-700">
                Eligible: <span className="font-semibold">{summary.eligible ?? 0}</span>
              </span>
              <span className="text-amber-600">
                Unsupported: <span className="font-semibold">{summary.unsupported ?? 0}</span>
              </span>
              <span className="text-gray-500">
                Out of Scope: <span className="font-semibold">{summary.out_of_scope ?? 0}</span>
              </span>
              <span className="text-red-500">
                Blocked: <span className="font-semibold">{summary.blocked ?? 0}</span>
              </span>
            </div>

            <DataTable
              columns={[
                { accessorKey: "name", header: "Asset" },
                {
                  accessorKey: "asset_type",
                  header: "Asset Type",
                  cell: ({ getValue }) => <span>{getValue() || "—"}</span>,
                },
                {
                  accessorKey: "status",
                  header: "VAPT Status",
                  cell: ({ getValue }) => <VaptStatusBadge status={getValue()} size="sm" />,
                },
                {
                  accessorKey: "reason",
                  header: "Reason",
                  cell: ({ getValue }) => <span>{getValue() || "—"}</span>,
                },
              ]}
              data={data.assets || []}
              getRowId={(a) => String(a.id)}
              emptyMessage="No assets are available in Asset Manager for this customer."
              enableColumnVisibility={false}
              maxBodyHeight="50vh"
            />
          </>
        )}

        <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
          <p className="text-[11px] text-gray-400">Read-only — no asset can be added or edited from this view.</p>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleSync} disabled={syncing}>
              {syncing ? "Syncing…" : "Sync Assets (refresh project from Asset Manager)"}
            </Button>
            <Button
              onClick={() => {
                onNavigateToScan && onNavigateToScan();
              }}
            >
              Start VAPT Scan
            </Button>
            <Button variant="secondary" onClick={handleRunScanNow} disabled={runningScan}>
              {runningScan ? "Running…" : "Run Scan Now (Automatic)"}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaptAssetInventoryModal;
